import { P2GGrid, P2GText, P2GTextField, useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem } from '@mui/material';
import Section from '~/components/core/section/section';
import { QUOTE_API, TranslationNamespaces } from '~/lib/constants';
import { fetchWithTenantAnalytics } from '~/lib/fetch';
import { CountryResponse, QuoteCountry } from '~/lib/types/basket-types';
import { useTenant, useAnalytics } from '@parcel2go/npm-p2g-app';
import CTAButton from '../core/button/cta-button';

type CountrySelectProps = {
  searchParams: { [k: string]: string };
  setSearchParams: (params: { [k: string]: string }) => void;
};

export function CountrySelect({ searchParams, setSearchParams }: CountrySelectProps) {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const isXs = useP2GMediaQueryOnly('xs');
  const { col: origin, dest: destination } = searchParams;
  const { session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const [origins, setOrigins] = useState<QuoteCountry[]>([]);
  const [destinations, setDestinations] = useState<QuoteCountry[]>([]);
  const [selectedOrigin, setSelectedOrigin] = useState<string | null>(searchParams.col || null);
  const [selectedDestination, setSelectedDestination] = useState<string | null>(searchParams.dest || null);

  useEffect(() => {
    async function getOrigins() {
      const response = await fetchWithTenantAnalytics(
        tenant,
        settings,
        session,
        `${QUOTE_API}/countries/origins?serviceSlug=${searchParams.slug}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch origins invalid http response ${response.status}`);
      }

      const result = (await response.json()) as CountryResponse;
      if (!result?.success || !result?.result?.countries) {
        throw new Error(`Failed to fetch origins invalid api response ${result.error?.message}`);
      }

      setOrigins(result.result.countries);
      if (result.result.countries.length > 0 && !selectedOrigin) {
        setSelectedOrigin(result.result.countries[0].iso3Code);
      }
    }

    async function getDestinations() {
      const response = await fetchWithTenantAnalytics(
        tenant,
        settings,
        session,
        `${QUOTE_API}/countries/destinations?serviceSlug=${searchParams.slug}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch destinations invalid http response ${response.status}`);
      }

      const result = (await response.json()) as CountryResponse;
      if (!result?.success || !result?.result?.countries) {
        throw new Error(`Failed to fetch destinations invalid api response ${result.error?.message}`);
      }

      setDestinations(result.result.countries);

      if (result.result.countries.length > 0 && !selectedDestination) {
        setSelectedDestination(result.result.countries[0].iso3Code);
      }
    }

    if (!origin) {
      getOrigins();
    }
    if (!destination) {
      getDestinations();
    }
  }, [destination, origin, tenant, searchParams, selectedOrigin, selectedDestination]);

  const continueDisabled = useMemo(
    () => !selectedDestination || !selectedOrigin,
    [selectedDestination, selectedOrigin]
  );

  const continueWithSelectedCountries = () => {
    if (selectedDestination && selectedOrigin) {
      setSearchParams({ ...searchParams, dest: selectedDestination, col: selectedOrigin });
    }
  };

  return (
    <P2GGrid
      container
      spacing={2}
      direction="column"
      xs={6}
      display="flex"
      justifyItems="center"
      alignContent="center"
      margin="0 auto"
    >
      <P2GGrid item>
        <Section
          title={t('COUNTRY_SELECT_TITLE', { defaultValue: 'Where are you sending to?' })}
          dataTestId="country-select-section"
          loading={false}
        >
          <P2GGrid container spacing={2}>
            <P2GGrid item xs={12}>
              <P2GText component="p" variant="body1">
                {t('COUNTRY_SELECT_DESCRIPTION', {
                  defaultValue: 'To help us calculate a price for this service, select the following:'
                })}
              </P2GText>
            </P2GGrid>
            {!origin && (
              <P2GGrid item xs={12}>
                <P2GTextField
                  label={`${t('ADDRESS_FIELD_ORIGIN_COUNTRY_LABEL', {
                    defaultValue: 'Send from'
                  })}*`}
                  value={selectedOrigin || ''}
                  onChange={(e) => setSelectedOrigin(e.target.value)}
                  fullWidth
                  select
                >
                  {origins.map((country) => (
                    <MenuItem key={country.iso3Code} value={country.iso3Code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </P2GTextField>
              </P2GGrid>
            )}
            {!destination && (
              <P2GGrid item xs={12}>
                <P2GTextField
                  label={`${t('ADDRESS_FIELD_DESTINATION_COUNTRY_LABEL', {
                    defaultValue: 'Send to'
                  })}*`}
                  value={selectedDestination || ''}
                  onChange={(e) => setSelectedDestination(e.target.value)}
                  fullWidth
                  select
                >
                  {destinations.map((country) => (
                    <MenuItem key={country.iso3Code} value={country.iso3Code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </P2GTextField>
              </P2GGrid>
            )}
            <P2GGrid item xs={12}>
              <CTAButton loading={continueDisabled} fullWidth={isXs} onClick={continueWithSelectedCountries} />
            </P2GGrid>
          </P2GGrid>
        </Section>
      </P2GGrid>
    </P2GGrid>
  );
}
